import { useCallback, useState } from 'react';
import { BotChatMessage, ChatMessage, Lang } from '../types';
import { generateAnswer } from '../api';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { putChat } from '../../../redux/voice-bot/chat-history.slice';
import { emptyChat } from '../../../redux/voice-bot/chat-history.slice';
import { ErrorHandler } from '../errorHandler';

interface UseChatMessageHandlerProps {
    versionEndpoint: string;
    lang: Lang;
    errorHandler: ErrorHandler;
}

export const useChatMessageHandler = ({ versionEndpoint, lang, errorHandler }: UseChatMessageHandlerProps) => {
    const dispatch = useAppDispatch();
    const { chats, selectedChatID } = useAppSelector((state) => state.chatHistory);
    const selectedChat = chats.find((chat) => chat.id === selectedChatID) || emptyChat();
    const [isLoading, setLoading] = useState(false);
    const [newMessageText, setNewMessageText] = useState('');

    const sendGenerateAnswer = useCallback(
        (messagesPayload: ChatMessage[]) => {
            setLoading(true);
            generateAnswer(
                versionEndpoint,
                lang,
                messagesPayload.map((msg) => ({
                    role: msg.type === 'user' ? 'user' : 'assistant',
                    content: msg.text,
                }))
            )
                .then((messages: BotChatMessage[]) => {
                    setNewMessageText('');

                    dispatch(
                        putChat({
                            ...selectedChat,
                            messages: messages.map(({ role, content }) => ({
                                type: role === 'user' ? 'user' : 'assistant',
                                text: content,
                            })),
                        })
                    );

                    setLoading(false);
                })
                .catch((error) => errorHandler.handle(error));
        },
        [versionEndpoint, lang, selectedChat, dispatch, errorHandler]
    );

    const handleSendNewMessage = useCallback(() => {
        sendGenerateAnswer([
            ...selectedChat.messages,
            {
                type: 'user',
                text: newMessageText,
            },
        ]);
    }, [sendGenerateAnswer, selectedChat.messages, newMessageText]);

    const handleRegenerate = useCallback(
        (idx: number) => {
            sendGenerateAnswer(selectedChat.messages.slice(0, idx + 1));
        },
        [sendGenerateAnswer, selectedChat.messages]
    );

    return {
        isLoading,
        newMessageText,
        setNewMessageText,
        handleSendNewMessage,
        handleRegenerate,
        selectedChat,
    };
};
